import React from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_PICKER_SHORT_MONTHS, SPRINT_TIMELINE_STATUS } from '../../../../shared/appConstants';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import sprintGoalIcon from '../../../../assets/images/sprint_goal.svg';
import OverflowTooltip from '../../../../shared/OverflowTooltip';

const SprintCard = ({ sprint, status, isSelected, isAvailable, onClick }) => {
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${DATE_PICKER_SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
  };

  const formattedStartDate = formatDate(sprint.start_date);
  const formattedEndDate = formatDate(sprint.end_date);
  
  const completedSp = sprint.complete_story_points || 0;
  const committedSp = sprint.total_story_points !== undefined ? sprint.total_story_points : ((sprint.complete_story_points || 0) + (sprint.incomplete_story_points || 0));
  const progressPercent = sprint.progress !== undefined ? sprint.progress : (committedSp > 0 ? Math.round((completedSp / committedSp) * 100) : 0);
  const velocity = sprint.velocity || 0;
  const unavailableMessage = {
    [SPRINT_TIMELINE_STATUS.ACTIVE]: t('sprintManagement.noActiveSprint'),
    [SPRINT_TIMELINE_STATUS.COMPLETED]: t('sprintManagement.noCompletedSprint'),
    [SPRINT_TIMELINE_STATUS.DRAFT]: t('sprintManagement.noDraftSprint'),
  }[status];

  return (
    <div
      className={`sm-stepper-card ${isSelected ? 'selected' : ''} ${status === SPRINT_TIMELINE_STATUS.DRAFT ? 'draft-card' : ''} ${!isAvailable ? 'disabled' : ''}`}
      onClick={isAvailable ? onClick : undefined}
      role="button"
      tabIndex={isAvailable ? 0 : -1}
      aria-disabled={!isAvailable}
      onKeyDown={isAvailable ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {!isAvailable ? (
        <div className="unavailable-sprint-message" role="status">
          <InfoOutlinedIcon fontSize="small" />
          <span>{unavailableMessage}</span>
        </div>
      ) : (
        <>
          <div className="card-header">
            <OverflowTooltip title={sprint.name} component="h3" className="sprint-name" placement="top-start" tooltipWidth={400} maxLines={2}>
          {sprint.name}
        </OverflowTooltip>
            <span className={`chip status-${status}`}>
              {t(`sprintTimeline.${status}Sprint`).split(' ')[0]}
            </span>
          </div>
          <div className="card-dates">
            <span>{formattedStartDate} - {formattedEndDate}</span>
            {status === SPRINT_TIMELINE_STATUS.ACTIVE && (
              <span className="progress-percent">{progressPercent}%</span>
            )}
          </div>
          <div className={`card-footer ${status !== SPRINT_TIMELINE_STATUS.ACTIVE ? 'footer-push' : ''}`}>
            {status === SPRINT_TIMELINE_STATUS.COMPLETED && (
              <span>{t('sprintManagement.velocity')}: {t('sprintManagement.velocitySp', { sp: velocity })}</span>
            )}
            {status === SPRINT_TIMELINE_STATUS.ACTIVE && (
              <div className="active-progress-container">
                <div className="progress-bar-wrapper">
                  <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
                </div>
                <div className="progress-text-bottom">
                  <span>{t('sprintManagement.velocityRatio', { done: completedSp, total: committedSp })}</span>
                </div>
              </div>
            )}
            {status === SPRINT_TIMELINE_STATUS.DRAFT && (
              <span>{t('sprintManagement.notStarted')}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SprintSelector = ({ selectedSprintStatus, onSelectSprint, apiData }) => {
  const { t } = useTranslation();

  const active = apiData?.data?.active || null;
  const completed = apiData?.data?.completed || null;
  const draft = apiData?.data?.draft || null;

  const buildSprintData = (sprint, status) => {
    return {
      ...(sprint || {}),
      status
    };
  };

  const completedData = buildSprintData(completed, SPRINT_TIMELINE_STATUS.COMPLETED);
  const activeData = buildSprintData(active, SPRINT_TIMELINE_STATUS.ACTIVE);
  const draftData = buildSprintData(draft, SPRINT_TIMELINE_STATUS.DRAFT);

  const renderStep = (data, isLast) => {
    const isAvailable = Boolean(data.id);
    const isSelected = isAvailable && selectedSprintStatus === data.status;

    return (
      <div key={data.status} className={`stepper-step ${!isAvailable ? 'disabled' : ''}`}>
        <div className="step-indicator">
          {data.status === SPRINT_TIMELINE_STATUS.COMPLETED && (
            <div className="circle circle-completed">
              <CheckIcon fontSize="small" />
            </div>
          )}
          {data.status === SPRINT_TIMELINE_STATUS.ACTIVE && (
            <div className="circle circle-active">
              <div className="inner-circle" />
            </div>
          )}
          {data.status === SPRINT_TIMELINE_STATUS.DRAFT && (
            <div className="circle circle-draft">
              <div className="inner-circle" />
            </div>
          )}

        </div>
        <SprintCard 
          sprint={data} 
          status={data.status} 
          isSelected={isSelected} 
          isAvailable={isAvailable}
          onClick={() => onSelectSprint(data.status)}
        />
      </div>
    );
  };

  const hasAnySprint = active?.id || completed?.id || draft?.id;

  return (
    <div className="sm-card sprint-selector-card">
      <div className="sm-card-header-with-icon">
        <img src={sprintGoalIcon} alt="" className="header-icon" />
        <h2 className="card-title">{t('sprintManagement.sprintTimeline')}</h2>
      </div>
      {hasAnySprint ? (
        <div className="stepper-container">
          {renderStep(completedData, false)}
          {renderStep(activeData, false)}
          {renderStep(draftData, true)}
        </div>
      ) : (
        <div className="sprint-selector-empty-state">
          <div className="empty-content">
            <span className="empty-text">{t('sprintTimeline.noSprints')}</span>
            <span className="empty-subtext">{t('sprintTimeline.noSprintsDescription')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SprintSelector;
